import { useSyncExternalStore } from 'react';
import type { IUser } from '@featit/shared';

/**
 * F-0709 slice 4 (ASIF-4, ASIF-12, ASIF-15; `docs/concept-start-a-work-area-as-if.md` §2.6): the as-if
 * view this tab holds. It lives in `sessionStorage` of the tab that started it, never `localStorage`
 * and never the refresh slot, so the person's other tabs stay themselves and closing the tab ends the
 * use. The credential carries no refresh secret: a 401 on it is the end of the view (expired, stopped,
 * or the work area's setting turned off), never a reason to refresh the person's own session.
 *
 * Nothing here opens a person store or writes to IndexedDB or `localStorage` (ASIF-12).
 */
export const AS_IF_VIEW_KEY = 'featit:asIfView';

export interface IAsIfView {
  workAreaId: string;
  /** The role, or null for a named member. */
  roleKey: string | null;
  /** The named member this is a view as, when the server says so; absent for a role. */
  memberUserId?: string | null;
  /** What the banner names: the role's name, or the member's. */
  roleName: string;
  /**
   * I-2193: the role's name in each language, as the person who started the view read it: the banner
   * has no right to read the package's words under the view's own token. Absent for a named member,
   * for a view started before this, and when the starter read English (no German word was known).
   */
  roleNames?: { en: string; de: string | null };
  accessToken: string;
  /** Epoch ms of the start. */
  startedAt: number;
  /**
   * ASIF-16: what the target holds that the starter's own rights do not cover (the ceiling's entries
   * count too), for a role and (I-2497) a named member; absent only for a view started before either,
   * which ends at the boot (`loadAsIfUi`).
   */
  limited?: string[];
  /** Epoch ms of the last request the view made: the expiry slides from it. */
  lastUsedAt: number;
  /**
   * Epoch ms the server says the view ends, read from `GET /auth/me` at the boot. Good until the view is
   * next used: the expiry slides from the use, and the web's own arithmetic takes over again.
   */
  boundExpiresAt?: number;
  /**
   * Who the person is, as the tab knew them when the view began. The reload that starts a view asks the
   * server for the person again, and a server that refuses a view that read leaves the page with no
   * one to be signed in as; this is what it falls back to.
   */
  person?: IUser;
}

/** Why the server ended a view, as the catalogue says it (`asIf.ended.because.*`); mapped by `asIfEnding.ts`. */
export type AsIfEndedBecauseType =
  | 'stopped'
  | 'permissionLost'
  | 'revoked'
  | 'expired'
  | 'originEnded'
  | 'settingOff'
  | 'startedBefore';

/** Why a view is over: it ran out, it was stopped, or the server no longer knows it. */
export type AsIfEndedReasonType = 'expired' | 'stopped' | 'refused';

export interface IAsIfState {
  view: IAsIfView | null;
  /** A view that ended in this tab and has not been acknowledged: the banner says so. */
  ended: {
    reason: AsIfEndedReasonType;
    roleName: string;
    /** I-2193: the view's `roleNames`, so the end is said in the reader's language too. */
    roleNames?: { en: string; de: string | null };
    detail?: string;
    /** Why, as the server's `as-if-ended` named it: the banner says it in the reader's language. */
    because?: AsIfEndedBecauseType;
  } | null;
  /** The sentence of the last refusal at a place a view cannot go (`as-if-not-here`), until left. */
  notHere: string | null;
}

/** A use closer than this to the last one writes nothing, as the server's slide does. */
const TOUCH_GRANULARITY_MS = 60 * 1000;

function readStored(): IAsIfView | null {
  try {
    const raw = window.sessionStorage.getItem(AS_IF_VIEW_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<IAsIfView>;
    if (
      typeof value.workAreaId === 'string' &&
      (typeof value.roleKey === 'string' || value.roleKey === null) &&
      typeof value.roleName === 'string' &&
      typeof value.accessToken === 'string' &&
      typeof value.startedAt === 'number' &&
      typeof value.lastUsedAt === 'number'
    ) {
      // Only this module writes it, so the optional parts (`person`, `limited`) are read as written.
      return value as IAsIfView;
    }
  } catch {
    // Unreadable storage: this tab holds no view.
  }
  return null;
}

export function writeStored(view: IAsIfView | null): void {
  try {
    if (view) window.sessionStorage.setItem(AS_IF_VIEW_KEY, JSON.stringify(view));
    else window.sessionStorage.removeItem(AS_IF_VIEW_KEY);
  } catch {
    // The view then lives in memory only, which still ends with the tab.
  }
}

let state: IAsIfState = { view: readStored(), ended: null, notHere: null };
const listeners = new Set<() => void>();

export function publish(next: IAsIfState): void {
  state = next;
  for (const listener of listeners) listener();
}

export function getAsIfState(): IAsIfState {
  return state;
}

export function subscribeAsIfState(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useAsIfState(): IAsIfState {
  return useSyncExternalStore(subscribeAsIfState, getAsIfState, getAsIfState);
}

/** The view's access token while this tab holds a live view, else null. */
export function asIfAccessToken(): string | null {
  return state.view?.accessToken ?? null;
}

/** I-2193: the view's role as the reader's language says it; the stored name where no word is held. */
export function asIfRoleNameIn(
  view: { roleName: string; roleNames?: { en: string; de: string | null } },
  locale: string,
): string {
  return (locale === 'de' ? view.roleNames?.de : null) ?? view.roleName;
}

export function beginAsIfView(view: Omit<IAsIfView, 'lastUsedAt'>, now = Date.now()): void {
  const held = { ...view, lastUsedAt: now };
  writeStored(held);
  publish({ view: held, ended: null, notHere: null });
}

/** A request the view made: the expiry slides from it. */
export function touchAsIfView(now = Date.now()): void {
  const view = state.view;
  if (!view || now - view.lastUsedAt < TOUCH_GRANULARITY_MS) return;
  const touched = { ...view, lastUsedAt: now, boundExpiresAt: undefined };
  writeStored(touched);
  publish({ ...state, view: touched });
}

/** A request went where a view cannot (`as-if-not-here`, ASIF-29): the page says why, with a way out. */
export function reportAsIfNotHere(message: string): void {
  if (state.view) publish({ ...state, notHere: message });
}

export function leaveAsIfNotHere(): void {
  if (state.notHere !== null) publish({ ...state, notHere: null });
}

/** For a test: forget the held view and everything said about it. */
export function resetAsIfViewForTest(): void {
  writeStored(null);
  publish({ view: null, ended: null, notHere: null });
}
